import * as React from "react";
import { CircleHelp } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * A question-mark trigger that explains a figure on hover or focus.
 *
 * Not a native `title` - see `ChartTooltip`'s own note on why: slow to
 * appear, unstyleable, and looks like an OS artefact next to everything else
 * in this product. This reuses that same floating-panel look (`.chart-tip`'s
 * surface, border and shadow) rather than inventing a second one.
 *
 * CSS-only rather than `usePopover`. That hook exists for interactive
 * floating panels that need a portal, outside-click dismissal and scroll
 * tracking - a dropdown that opens and has to be closed again. This is
 * read-only text that appears on `:hover`/`:focus-visible` and disappears
 * the moment either leaves, which a sibling selector already does without a
 * portal or a click handler.
 */
export function InfoHint({
  text,
  icon,
  label = "What this figure means",
  className,
}: {
  text: string;
  /**
   * The trigger's glyph. Defaults to the question mark.
   *
   * The two are not the same offer. A question mark asks "what does this figure
   * mean" and every stat tile in the console uses it that way; an `i` says there
   * is more of the record to read here, which is what the ladder's advisory is
   * behind. Overriding it keeps one floating-panel look for both rather than a
   * second component for the second kind of hint.
   */
  icon?: React.ReactNode;
  /** What the trigger announces. The default names a figure; a hint about
   * something else should say what. */
  label?: string;
  className?: string;
}) {
  const id = React.useId();

  return (
    <span className={cn("info-hint", className)}>
      <button type="button" className="info-hint__trigger" aria-describedby={id}>
        {icon ?? <CircleHelp aria-hidden />}
        <span className="sr-only">{label}</span>
      </button>
      <span role="tooltip" id={id} className="info-hint__bubble">
        {text}
      </span>
    </span>
  );
}
